/**
 * Componente para mostrar peticiones relacionadas con una orden
 * Diseño profesional con iconografía y carga autónoma
 * Responsive: tabla en desktop, cards en móvil
 */
import React, { useState, useEffect } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useMediaQuery,
    useTheme,
    Divider,
    Skeleton,
    Fade
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import InventoryIcon from "@mui/icons-material/Inventory";
import TagIcon from "@mui/icons-material/Tag";
import ListAltIcon from "@mui/icons-material/ListAlt";
import NumbersIcon from "@mui/icons-material/Numbers";
import PeticionesService from "../../api/peticiones.api";
import EstadoBadge from "../ui/EstadoBadge";

const PeticionesRelacionadas = ({ orderNum }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
    const [peticiones, setPeticiones] = useState([]);
    const [loading, setLoading] = useState(true);

    // Cargar peticiones
    useEffect(() => {
        const cargarPeticiones = async () => {
            if (!orderNum) {
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                const data = await PeticionesService.listPorAlbaran(orderNum);
                setPeticiones(data || []);
            } catch (error) {
                console.error('Error al cargar peticiones:', error);
                setPeticiones([]);
            } finally {
                setLoading(false);
            }
        };

        cargarPeticiones();
    }, [orderNum]);

    if (loading) {
        return (
            <Box sx={{ mt: 3 }}>
                {/* Header skeleton */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 2.5,
                        p: 2,
                        backgroundColor: "#f8f9fa",
                        borderRadius: 2,
                        border: "1px solid #e9ecef",
                    }}
                >
                    <Skeleton variant="rounded" width={36} height={36} />
                    <Box sx={{ flex: 1 }}>
                        <Skeleton width="50%" height={12} sx={{ mb: 0.5 }} />
                        <Skeleton width="30%" height={18} />
                    </Box>
                </Box>

                {/* Content skeleton */}
                {isMobile ? (
                    <Stack spacing={2}>
                        {[1, 2].map((i) => (
                            <Box
                                key={i}
                                sx={{
                                    p: 2,
                                    backgroundColor: "#fff",
                                    borderRadius: 2,
                                    border: "1px solid #e9ecef",
                                }}
                            >
                                <Stack spacing={1.5}>
                                    <Skeleton width="60%" height={16} />
                                    <Skeleton width="80%" height={14} />
                                    <Skeleton width="40%" height={14} />
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                ) : (
                    <Box
                        sx={{
                            backgroundColor: "#fff",
                            borderRadius: 2,
                            border: "1px solid #e9ecef",
                            p: 2,
                        }}
                    >
                        <Stack spacing={1.5}>
                            <Skeleton width="100%" height={40} />
                            <Skeleton width="100%" height={50} />
                            <Skeleton width="100%" height={50} />
                        </Stack>
                    </Box>
                )}
            </Box>
        );
    }

    if (!peticiones || peticiones.length === 0) {
        return null;
    }

    // Componente para campo de info con icono (móvil)
    const InfoField = ({ icon: Icon, label, children }) => (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Icon sx={{ fontSize: 16, color: "#6c757d", mt: 0.25 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#6c757d",
                        textTransform: "uppercase",
                        letterSpacing: "0.3px",
                        mb: 0.25
                    }}
                >
                    {label}
                </Typography>
                {children}
            </Box>
        </Box>
    );

    return (
        <Fade in={!loading} timeout={500}>
            <Box sx={{ mt: 3 }}>
                <Card
                    sx={{
                        backgroundColor: "#fff",
                        borderRadius: 2,
                        border: "1px solid #e9ecef",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 2.5,
                            backgroundColor: "#f8f9fa",
                            borderBottom: "2px solid #e9ecef",
                        }}
                    >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        backgroundColor: "#fff",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.06)"
                    }}
                >
                    <ListAltIcon sx={{ fontSize: 20, color: "#cc071e" }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography
                        sx={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: "#6c757d",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            mb: 0.25
                        }}
                    >
                        Peticiones relacionadas
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 16,
                            fontWeight: 700,
                            color: "#212529",
                            lineHeight: 1.2
                        }}
                    >
                        {peticiones.length} {peticiones.length === 1 ? 'petición' : 'peticiones'}
                    </Typography>
                </Box>
                    </Box>

                    {/* Listado de peticiones */}
                    <CardContent sx={{ p: 2 }}>
            {isMobile ? (
                // Vista móvil: Cards
                <Stack spacing={2}>
                    {peticiones.map((peticion) => (
                        <Card
                            key={peticion.id}
                            sx={{
                                backgroundColor: "#fff",
                                borderRadius: 2,
                                border: "1px solid #e9ecef",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                                overflow: "hidden",
                                transition: "all 0.2s",
                                "&:hover": {
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                                    transform: "translateY(-2px)"
                                },
                            }}
                        >
                            {/* Header de la card */}
                            <Box
                                sx={{
                                    p: 2,
                                    backgroundColor: "#f8f9fa",
                                    borderBottom: "1px solid #e9ecef",
                                }}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <TagIcon sx={{ fontSize: 18, color: "#495057" }} />
                                        <Typography
                                            sx={{
                                                fontSize: 15,
                                                fontWeight: 700,
                                                color: "#212529"
                                            }}
                                        >
                                            {peticion.referencia}
                                        </Typography>
                                    </Stack>
                                    <EstadoBadge estado={peticion.estado} />
                                </Stack>
                            </Box>

                            {/* Contenido de la card */}
                            <CardContent sx={{ p: 2 }}>
                                <Stack spacing={1.5}>
                                    {/* Producto */}
                                    <InfoField icon={InventoryIcon} label="Producto">
                                        <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212529" }}>
                                            {peticion.nombreProducto || '-'}
                                        </Typography>
                                    </InfoField>

                                    {/* Unidades */}
                                    <InfoField icon={NumbersIcon} label="Unidades">
                                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#cc071e" }}>
                                            {peticion.unidades}
                                        </Typography>
                                    </InfoField>

                                    {/* Reponedor */}
                                    {peticion.reponedor && (
                                        <>
                                            <Divider sx={{ my: 0.5 }} />
                                            <InfoField icon={PersonIcon} label="Reponedor">
                                                <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212529" }}>
                                                    {peticion.reponedor.username}
                                                </Typography>
                                            </InfoField>
                                        </>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            ) : (
                // Vista desktop: Tabla profesional
                <Box
                    sx={{
                        backgroundColor: "#fff",
                        borderRadius: 2,
                        border: "1px solid #e9ecef",
                        overflow: "hidden",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    }}
                >
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow
                                    sx={{
                                        backgroundColor: "#f8f9fa",
                                        borderBottom: "2px solid #e9ecef"
                                    }}
                                >
                                    <TableCell
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: 12,
                                            color: "#495057",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px",
                                            py: 1.5
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                            <TagIcon sx={{ fontSize: 16, color: "#6c757d" }} />
                                            Referencia
                                        </Box>
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: 12,
                                            color: "#495057",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px",
                                            py: 1.5
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                            <InventoryIcon sx={{ fontSize: 16, color: "#6c757d" }} />
                                            Producto
                                        </Box>
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: 12,
                                            color: "#495057",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px",
                                            py: 1.5
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75 }}>
                                            <NumbersIcon sx={{ fontSize: 16, color: "#6c757d" }} />
                                            Unidades
                                        </Box>
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: 12,
                                            color: "#495057",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px",
                                            py: 1.5
                                        }}
                                    >
                                        Estado
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: 12,
                                            color: "#495057",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px",
                                            py: 1.5
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                            <PersonIcon sx={{ fontSize: 16, color: "#6c757d" }} />
                                            Reponedor
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {peticiones.map((peticion, index) => (
                                    <TableRow
                                        key={peticion.id}
                                        sx={{
                                            '&:last-child td': { borderBottom: 0 },
                                            '&:hover': {
                                                backgroundColor: "#f8f9fa",
                                            },
                                            transition: "background-color 0.2s",
                                            borderBottom: index < peticiones.length - 1 ? "1px solid #e9ecef" : "none"
                                        }}
                                    >
                                        <TableCell
                                            sx={{
                                                fontSize: 14,
                                                color: "#212529",
                                                fontWeight: 600,
                                                py: 2
                                            }}
                                        >
                                            {peticion.referencia}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontSize: 14,
                                                color: "#495057",
                                                py: 2
                                            }}
                                        >
                                            {peticion.nombreProducto || '-'}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                fontSize: 15,
                                                color: "#cc071e",
                                                fontWeight: 700,
                                                py: 2
                                            }}
                                        >
                                            {peticion.unidades}
                                        </TableCell>
                                        <TableCell align="center" sx={{ py: 2 }}>
                                            <EstadoBadge estado={peticion.estado} />
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontSize: 14,
                                                color: "#495057",
                                                py: 2
                                            }}
                                        >
                                            {peticion.reponedor?.username || '-'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
                    </CardContent>
                </Card>
        </Box>
        </Fade>
    );
};

export default PeticionesRelacionadas;
